import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "i18n-fe";
import { formatPoints, movementDisplayNumber } from "score-common";
import { formatRideLabel } from "./RideLink";
import useVenueDateTime from "../hooks/useVenueDateTime";
import { useScoreApi, unwrap } from "../api/scoreApi";
import {
  buildVersionsComparison,
  hasHeldCopy,
  movementNameFromTest,
  pickCarriesSignature,
  versionsFromHeldCopy,
  versionsFromSeat,
  versionsNeedingPreview,
  withPreviewedTotals,
} from "../util/sheetVersions";

// #3478 - the Review Queue's Sheet versions screen (GLOSSARY "Sheet version",
// ADR 0002): every version of one judge's sheet side by side, movement by
// movement, differing rows marked, and a whole-version pick that goes through
// POST /scoresheet/:id/pick-version (#3477, pickSheetVersion). It replaces the
// old Compare button (which 400'd) and the raw "Keep current / Apply attempted"
// rows. The words are the show office's: no "held copy" jargon beyond the one
// label, no "authoritative", no "contender".

const EMPTY_VALUE = "–"; // en dash

const WHEN_FORMAT = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

// Static key tables: i18n keys are never built from data.
const ORIGIN_KEY = {
  scribe: "sheetVersions.whoOptions.scribe",
  admin: "sheetVersions.whoOptions.admin",
  fvd: "sheetVersions.whoOptions.fvd",
};

const OTHER_DIFFERENCE_KEY = {
  collectives: "sheetVersions.otherDifferences.collectives",
  eliminations: "sheetVersions.otherDifferences.eliminations",
  errors: "sheetVersions.otherDifferences.errors",
  generalRemarks: "sheetVersions.otherDifferences.generalRemarks",
  annotatedPurpose: "sheetVersions.otherDifferences.annotatedPurpose",
  annotatedIntroduce: "sheetVersions.otherDifferences.annotatedIntroduce",
};

const MARK_DIFF_KEY = {
  score: "sheetVersions.markDiff.score",
  deductions: "sheetVersions.markDiff.deductions",
  flagged: "sheetVersions.markDiff.flagged",
  remarks: "sheetVersions.markDiff.remarks",
  annotatedDirective: "sheetVersions.markDiff.annotatedDirective",
  annotatedDescription: "sheetVersions.markDiff.annotatedDescription",
};

const PICK_ERROR_KEY = {
  placed_class_locked: "sheetVersions.errors.placed_class_locked",
  pick_test_unavailable: "sheetVersions.errors.pick_test_unavailable",
  concurrent_modification: "sheetVersions.errors.concurrent_modification",
  missing_signature: "sheetVersions.errors.missing_signature",
  validation_error: "sheetVersions.errors.validation_error",
  elimination_change_needs_sheet_edit: "sheetVersions.errors.elimination_change_needs_sheet_edit",
  def_changed_since_confirm: "sheetVersions.errors.def_changed_since_confirm",
  already_authoritative: "sheetVersions.errors.already_authoritative",
  no_conflict: "sheetVersions.errors.no_conflict",
  no_contender: "sheetVersions.errors.no_contender",
};

function pickErrorKey(err) {
  return PICK_ERROR_KEY[err?.response?.data?.code] ?? "sheetVersions.errors.default";
}

function kindKey(version, isHeldMode) {
  if (version.kind === "held_copy") return "sheetVersions.kind.held";
  return isHeldMode || version.standing ? "sheetVersions.kind.standing" : "sheetVersions.kind.other";
}

function VersionHeader({ version, isHeldMode, canPick, onPick }) {
  const { t } = useTranslation("scoreadmin");
  const fmtWhen = useVenueDateTime(WHEN_FORMAT);
  const hasTotal = version.total !== null;
  const mayPick = canPick && (version.pick !== null);
  return (
    <Box
      sx={{ flex: 1, minWidth: 0, border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}
      data-testid={`sheet-version-${version.key}`}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" color="text.primary" data-testid="sheet-version-kind">
          {t(kindKey(version, isHeldMode))}
        </Typography>
        {version.standing && !isHeldMode && (
          <Chip size="small" color="success" label={t("sheetVersions.standingChip")} data-testid="sheet-version-standing" />
        )}
        <Chip
          size="small"
          variant={version.signed ? "filled" : "outlined"}
          color={version.signed ? "success" : "default"}
          label={t(version.signed ? "sheetVersions.signed" : "sheetVersions.notSigned")}
          data-testid="sheet-version-signed"
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" data-testid="sheet-version-who">
        {t("sheetVersions.who")}: {t(ORIGIN_KEY[version.origin] ?? "sheetVersions.whoOptions.unknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid="sheet-version-tablet">
        {t("sheetVersions.tablet")}: {version.device || t("sheetVersions.tabletUnknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid="sheet-version-when">
        {t("sheetVersions.when")}: {version.when ? fmtWhen(version.when) : EMPTY_VALUE}
      </Typography>
      <Typography variant="body2" color="text.primary" data-testid="sheet-version-total">
        {t("sheetVersions.total")}:{" "}
        {hasTotal
          ? `${formatPoints(version.total)}${version.max !== null ? ` / ${formatPoints(version.max)}` : ""}`
          : t("sheetVersions.totalNotWorkedOut")}
      </Typography>
      {version.totalFromPreview && (
        <Typography variant="caption" color="text.secondary" display="block" data-testid="sheet-version-total-note">
          {t("sheetVersions.totalWorkedOutNote")}
        </Typography>
      )}
      {mayPick && (
        <Button
          size="small"
          variant="contained"
          sx={{ mt: 1.5 }}
          onClick={() => onPick(version)}
          data-testid="sheet-version-pick-button"
        >
          {t(version.standing ? "sheetVersions.keepButton" : "sheetVersions.pickButton")}
        </Button>
      )}
    </Box>
  );
}

function MarkCell({ mark, t }) {
  if (!mark) {
    return (
      <TableCell sx={{ color: "text.secondary" }}>{t("sheetVersions.noMark")}</TableCell>
    );
  }
  return <TableCell>{mark.score ?? EMPTY_VALUE}</TableCell>;
}

export default function SheetVersionsDialog({ open, onClose, item, canPick, onPicked }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const subject = item?.subject ?? {};
  const isHeldMode = subject.type === "stale_write";
  const [state, setState] = useState({ status: "loading", versions: [] });
  // #3484: what the screen learns after the versions are on it. The sheet's Test
  // (movement names) and the server's dry-run totals for versions that have none
  // of their own. Both are extras: a failed load leaves the screen as it was.
  const [extras, setExtras] = useState({ test: null, previews: {} });
  const [confirmVersion, setConfirmVersion] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [pickError, setPickError] = useState(null);

  const subjectId = subject.id;
  const rideId = subject.rideId;
  const judgeId = subject.judgeId;

  useEffect(() => {
    if (!open || !subjectId) return undefined;
    let cancelled = false;
    setState({ status: "loading", versions: [] });
    setExtras({ test: null, previews: {} });
    setPickError(null);
    (async () => {
      try {
        let versions;
        if (isHeldMode) {
          const row = unwrap(await scoreApi.scoreSheets.getScoreSheet(subjectId));
          versions = hasHeldCopy(row) ? versionsFromHeldCopy(row) : [];
        } else {
          const sheets = unwrap(await scoreApi.scoreSheets.listScoreSheets(rideId));
          versions = versionsFromSeat(sheets, judgeId);
          if (versions.length < 2) versions = [];
        }
        if (!cancelled) setState({ status: versions.length ? "ready" : "empty", versions });
        if (cancelled || versions.length === 0) return;
        const testId = versions.find((v) => v.testId)?.testId;
        const [testResult, ...previewResults] = await Promise.allSettled([
          testId ? scoreApi.tests.getTest(testId).then(unwrap) : Promise.resolve(null),
          ...versionsNeedingPreview(versions).map(async (v) => [
            v.key,
            unwrap(await scoreApi.scoreSheets.previewSheetVersionTotals(v.pick.version, v.pick.id)),
          ]),
        ]);
        if (cancelled) return;
        const previews = {};
        for (const r of previewResults) if (r.status === "fulfilled") previews[r.value[0]] = r.value[1];
        setExtras({
          test: testResult.status === "fulfilled" ? testResult.value : null,
          previews,
        });
      } catch {
        if (!cancelled) setState({ status: "error", versions: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
    // scoreApi is a fresh object per render of useScoreApi's host; the item's
    // own ids are what a reload keys on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subjectId, rideId, judgeId, isHeldMode]);

  const versions = withPreviewedTotals(state.versions, extras.previews);
  const comparison = buildVersionsComparison(versions);
  const differingCount = comparison.movementRows.filter((r) => r.differs).length;
  const carries = pickCarriesSignature(versions, confirmVersion);

  function closeConfirm() {
    setConfirmVersion(null);
    setNote("");
  }

  async function handleConfirm() {
    if (!confirmVersion?.pick) return;
    setSaving(true);
    setPickError(null);
    try {
      const body = { version: confirmVersion.pick.version };
      if (note.trim()) body.note = note.trim();
      const result = unwrap(await scoreApi.scoreSheets.pickSheetVersion(confirmVersion.pick.id, body));
      // `signatureCarried` is only described in the route's docs, not typed:
      // read it defensively.
      const signatureCarried = result?.signatureCarried === true;
      closeConfirm();
      onPicked?.({ result, signatureCarried });
    } catch (err) {
      closeConfirm();
      setPickError(pickErrorKey(err));
    } finally {
      setSaving(false);
    }
  }

  const confirmBodyKey = !confirmVersion
    ? null
    : confirmVersion.kind === "held_copy"
      ? "sheetVersions.confirm.pickedHeldCopy"
      : isHeldMode
        ? "sheetVersions.confirm.pickedKeepSheet"
        : "sheetVersions.confirm.pickedOther";

  const rideLabel = formatRideLabel(subject, t("rideEditor.onHorse"));
  const judgeLabel = subject.judgeName || t("common.unknownJudge");

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen data-testid="sheet-versions-screen">
        <DialogTitle sx={{ pr: 7 }}>
          {t("sheetVersions.title", { judge: judgeLabel })}
          <Typography variant="body2" color="text.secondary" component="div">
            {t("sheetVersions.subtitle", { ride: rideLabel || EMPTY_VALUE })}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label={t("sheetVersions.close")}
            sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {pickError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="sheet-versions-error">
              {t(pickError)}
            </Alert>
          )}
          {state.status === "loading" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <Typography color="text.secondary">{t("sheetVersions.loading")}</Typography>
            </Box>
          )}
          {state.status === "error" && (
            <Alert severity="error" data-testid="sheet-versions-load-error">
              {t("sheetVersions.loadError")}
            </Alert>
          )}
          {state.status === "empty" && (
            <Alert severity="info" data-testid="sheet-versions-empty">
              {t("sheetVersions.nothingToPick")}
            </Alert>
          )}
          {state.status === "ready" && (
            <>
              {!canPick && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="sheet-versions-no-permission">
                  {t("sheetVersions.noPermission")}
                </Alert>
              )}
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                {versions.map((version) => (
                  <VersionHeader
                    key={version.key}
                    version={version}
                    isHeldMode={isHeldMode}
                    canPick={canPick}
                    onPick={setConfirmVersion}
                  />
                ))}
              </Stack>

              {differingCount === 0 && comparison.otherDifferences.length === 0 && (
                <Typography color="text.secondary" sx={{ mb: 1 }} data-testid="sheet-versions-no-differences">
                  {t("sheetVersions.noDifferences")}
                </Typography>
              )}
              {comparison.otherDifferences.length > 0 && (
                <Typography color="text.primary" sx={{ mb: 1 }} data-testid="sheet-versions-other-differences">
                  {t("sheetVersions.otherDifferencesLabel")}{" "}
                  {comparison.otherDifferences.map((f) => t(OTHER_DIFFERENCE_KEY[f])).join(", ")}
                </Typography>
              )}

              <TableContainer>
                <Table size="small" data-testid="sheet-versions-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("sheetVersions.movementColumn")}</TableCell>
                      {versions.map((version) => (
                        <TableCell key={version.key}>{t(kindKey(version, isHeldMode))}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparison.movementRows.map((row) => {
                      const hints = row.differingFields;
                      return (
                        <TableRow
                          key={row.num}
                          data-testid="sheet-versions-row"
                          data-differs={row.differs ? "true" : "false"}
                          sx={row.differs ? { bgcolor: "action.selected" } : undefined}
                        >
                          <TableCell>
                            {t("sheetVersions.movement", { number: movementDisplayNumber(extras.test, row.num) })}
                            {movementNameFromTest(extras.test, row.num) && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                component="div"
                                data-testid="sheet-versions-row-name"
                              >
                                {movementNameFromTest(extras.test, row.num)}
                              </Typography>
                            )}
                            {row.differs && (
                              <Chip
                                size="small"
                                color="warning"
                                label={t("sheetVersions.markedRow")}
                                sx={{ ml: 1 }}
                                data-testid="sheet-versions-row-marker"
                              />
                            )}
                            {hints.length > 0 && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                data-testid="sheet-versions-row-hint"
                              >
                                {t("sheetVersions.differsInLabel")} {hints.map((f) => t(MARK_DIFF_KEY[f])).join(", ")}
                              </Typography>
                            )}
                          </TableCell>
                          {row.marks.map((mark, idx) => (
                            <MarkCell key={versions[idx].key} mark={mark} t={t} />
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("sheetVersions.close")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmVersion)} onClose={saving ? undefined : closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>{t("sheetVersions.confirm.title")}</DialogTitle>
        <DialogContent data-testid="sheet-versions-confirm">
          {confirmBodyKey && (
            <Typography sx={{ mb: 1 }} data-testid="sheet-versions-confirm-body">
              {t(confirmBodyKey, { judge: judgeLabel })}
            </Typography>
          )}
          <Typography sx={{ mb: 1 }} color="text.secondary" data-testid="sheet-versions-confirm-undo">
            {t("sheetVersions.confirm.undo")}
          </Typography>
          {carries && (
            <Alert severity="info" sx={{ mb: 1 }} data-testid="sheet-versions-confirm-signature">
              {t("sheetVersions.confirm.signatureCarries")}
            </Alert>
          )}
          <Typography sx={{ mb: 2 }} color="text.secondary">
            {t("sheetVersions.confirm.totalsAgain")}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={t("sheetVersions.confirm.noteLabel")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ "data-testid": "sheet-versions-confirm-note" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={saving}>
            {t("sheetVersions.confirm.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            loading={saving}
            variant="contained"
            data-testid="sheet-versions-confirm-button"
          >
            {t("sheetVersions.confirm.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
